import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector, useDispatch } from "react-redux";
import { removeItem, setQty } from '../store/cartSlice.js';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { fetchAddresses } from '../store/addressSlice.js';

const Cart = () => {
  const items = useSelector((s) => s.cart.items || []);
  const user = useSelector((s) => s.auth.user);
  const addresses = useSelector((s) => s.addresses.items);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchAddresses(user.uid));
    }
  }, [dispatch, user]);

  if (!user) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="card border-0 shadow-sm rounded-4 p-5">
              <i className="bi bi-lock display-4 text-muted mb-3"></i>
              <h3 className="fw-bold mb-3">Sign In Required</h3>
              <p className="text-muted mb-4">Please sign in to view your shopping cart</p>
              <button
                className="btn btn-dark rounded-pill px-4 py-3"
                style={{ background: "#2f0f0c" }}
                onClick={() => navigate("/signin")}
              >
                <i className="bi bi-person-circle me-2"></i>
                Sign In to Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = items.reduce(
    (sum, item) => sum + (item.price || 0) * (item.qty || 1),
    0,
  );
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  if (items.length === 0) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="card border-0 shadow-sm rounded-4 p-5">
              <i className="bi bi-cart-x display-4 text-muted mb-3"></i>
              <h3 className="fw-bold mb-3">Your Cart is Empty</h3>
              <p className="text-muted mb-4">
                Discover our exquisite collection of premium perfumes
              </p>
              <button
                className="btn btn-dark rounded-pill px-4 py-3"
                style={{ background: "#2f0f0c" }}
                onClick={() => navigate("/perfumes")}
              >
                <i className="bi bi-shop me-2"></i>
                Start Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <style>{`
        .cart-item {
          transition: all 0.3s ease;
        }
        .cart-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .quantity-btn {
          transition: all 0.2s ease;
        }
        .quantity-btn:hover {
          transform: scale(1.1);
        }
        .checkout-card {
          background: linear-gradient(135deg, #f8f9fa, #ffffff);
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">
          <i className="bi bi-cart-check me-2 text-primary"></i>
          Shopping Cart
        </h2>
        <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2">
          {items.length} item{items.length > 1 ? 's' : ''}
        </span>
      </div>

      <div className="row g-4">
        {/* CART ITEMS */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm rounded-4 p-4">
            {items.map((item, index) => (
              <div
                key={item.id}
                className={`cart-item d-flex gap-3 p-3 rounded-3 ${index < items.length - 1 ? 'mb-3 border-bottom' : ''}`}
              >
                <div className="position-relative flex-shrink-0">
                  <img
                    src={item.image || "/slider/1.jpeg"}
                    alt={item.name}
                    className="rounded-3 cart-item-img"
                    style={{ width: "100px", height: "100px", objectFit: "cover" }}
                  />
                  <span className="badge bg-dark position-absolute top-0 start-0 m-1" style={{ fontSize: 10 }}>
                    #{index + 1}
                  </span>
                </div>
                
                <div className="flex-grow-1 min-w-0">
                  <h6 className="fw-bold mb-1 text-truncate">{item.name}</h6>
                  <p className="text-muted small mb-2 d-none d-md-block">{item.description || "Premium fragrance"}</p>
                  
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                    {item.specialOffer ? (
                      <div className="d-flex align-items-center gap-1 flex-wrap">
                        <span className="text-decoration-line-through text-muted small">₹{item.originalPrice?.toLocaleString()}</span>
                        <span className="fw-bold text-success">₹{item.price?.toLocaleString()}</span>
                        <span className="badge bg-success-subtle text-success" style={{ fontSize: 10 }}>{item.specialOffer.discount}% OFF</span>
                      </div>
                    ) : (
                      <span className="fw-bold text-success">₹{item.price?.toLocaleString()}</span>
                    )}
                  </div>

                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="input-group" style={{ width: 110 }}>
                      <button className="btn btn-outline-secondary btn-sm quantity-btn"
                        onClick={() => dispatch(setQty({ id: item.id, qty: Math.max(1, (item.qty || 1) - 1) }))}>
                        <i className="bi bi-dash"></i>
                      </button>
                      <input type="text" className="form-control form-control-sm text-center fw-bold" value={item.qty || 1} readOnly />
                      <button className="btn btn-outline-secondary btn-sm quantity-btn"
                        onClick={() => dispatch(setQty({ id: item.id, qty: (item.qty || 1) + 1 }))}>
                        <i className="bi bi-plus"></i>
                      </button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-success small">₹{(item.price * (item.qty || 1)).toLocaleString()}</span>
                      <button className="btn btn-outline-danger btn-sm rounded-pill px-2"
                        onClick={() => { dispatch(removeItem(item.id)); toast.success("Item removed"); }}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ORDER SUMMARY */}
        <div className="col-lg-4">
          <div className="position-sticky" style={{ top: "100px" }}>
            {/* Quick Address Preview */}
            {addresses.length > 0 && (
              <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                <h6 className="fw-bold mb-3">
                  <i className="bi bi-geo-alt me-2 text-primary"></i>
                  Quick Delivery Preview
                </h6>
                <div className="bg-light rounded-3 p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="fw-semibold">{addresses[0].label}</div>
                      <small className="text-muted">{addresses[0].city}, {addresses[0].state}</small>
                    </div>
                    <button 
                      className="btn btn-sm btn-outline-primary rounded-pill"
                      onClick={() => navigate('/addresses')}
                    >
                      Change
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="card border-0 shadow-sm rounded-4 p-4 checkout-card">
              <h5 className="fw-bold mb-4">
                <i className="bi bi-receipt me-2 text-success"></i>
                Order Summary
              </h5>

              <div className="d-flex justify-content-between mb-3">
                <span>Subtotal ({items.length} items)</span>
                <span className="fw-semibold">₹{subtotal.toLocaleString()}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>GST (18%)</span>
                <span className="fw-semibold">₹{gst.toLocaleString()}</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>Delivery Charges</span>
                <span className="text-success fw-semibold">FREE</span>
              </div>
              
              <div className="d-flex justify-content-between mb-3">
                <span>Packaging</span>
                <span className="text-success fw-semibold">FREE</span>
              </div>
              
              <hr className="my-3" />
              
              <div className="d-flex justify-content-between mb-4 fs-4 fw-bold">
                <span>Total Amount</span>
                <span className="text-success">₹{total.toLocaleString()}</span>
              </div>
              
              <div className="alert alert-info border-0 rounded-3 mb-4">
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <small>GST (18%) included • Free delivery & packaging</small>
                </div>
              </div>

              <div className="d-grid gap-2">
                <button
                  className="btn btn-dark btn-lg rounded-pill py-3 fw-bold"
                  style={{ background: "linear-gradient(135deg, #2f0f0c, #4a1a15)" }}
                  onClick={() => navigate("/checkout")}
                >
                  <i className="bi bi-credit-card me-2"></i>
                  Proceed to Checkout
                </button>
                
                <button
                  className="btn btn-outline-dark rounded-pill py-3"
                  onClick={() => navigate("/perfumes")}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Continue Shopping
                </button>
                
                {addresses.length === 0 && (
                  <button
                    className="btn btn-outline-primary rounded-pill py-2"
                    onClick={() => navigate('/addresses')}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Delivery Address
                  </button>
                )}
              </div>
              
              <div className="text-center mt-3">
                <small className="text-muted">
                  <i className="bi bi-shield-check me-1"></i>
                  Secure checkout with 256-bit SSL encryption
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
